/**
 * Main CRUD storage class
 * Wraps a storage adapter and provides automatic timestamp management
 */
class CRUDStorage {
    constructor(adapter, options = {}) {
        this.adapter = adapter;
        this.options = {
            timestamps: true,
            idField: 'id',
            ...options,
        };
    }
    /**
     * Add timestamps to an item
     */
    addTimestamps(item, isUpdate = false) {
        if (!this.options.timestamps) {
            return item;
        }
        const timestamp = new Date().toISOString();
        if (isUpdate) {
            return {
                ...item,
                updatedAt: timestamp,
            };
        }
        return {
            ...item,
            createdAt: timestamp,
            updatedAt: timestamp,
        };
    }
    /**
     * Create a new item
     */
    async create(item) {
        const itemWithTimestamps = this.addTimestamps(item, false);
        return this.adapter.create(itemWithTimestamps);
    }
    /**
     * Read a single item by ID
     */
    async read(id) {
        return this.adapter.read(id);
    }
    /**
     * Update an existing item
     */
    async update(id, data) {
        const dataWithTimestamp = this.addTimestamps(data, true);
        return this.adapter.update(id, dataWithTimestamp);
    }
    /**
     * Delete an item by ID
     */
    async delete(id) {
        return this.adapter.delete(id);
    }
    /**
     * List items with pagination and filtering
     */
    async list(options) {
        return this.adapter.list(options);
    }
    /**
     * Get total count of items
     */
    async count(filter) {
        return this.adapter.count(filter);
    }
    /**
     * Get all items (convenience method)
     */
    async getAll() {
        const result = await this.adapter.list({ limit: Number.MAX_SAFE_INTEGER });
        return result.items;
    }
    /**
     * Check if an item exists
     */
    async exists(id) {
        const item = await this.read(id);
        return item !== null;
    }
    /**
     * Find items by filter
     */
    async find(filter) {
        const result = await this.adapter.list({ filter, limit: Number.MAX_SAFE_INTEGER });
        return result.items;
    }
    /**
     * Find single item by filter
     */
    async findOne(filter) {
        const result = await this.adapter.list({ filter, limit: 1 });
        return result.items[0] || null;
    }
}

/**
 * LocalStorage adapter for CRUD operations
 * Stores data in browser's localStorage as JSON
 */
class LocalStorageAdapter {
    constructor(entityName) {
        this.storageKey = entityName;
        this.initializeStorage();
    }
    initializeStorage() {
        if (!localStorage.getItem(this.storageKey)) {
            localStorage.setItem(this.storageKey, JSON.stringify([]));
        }
    }
    getItems() {
        try {
            const data = localStorage.getItem(this.storageKey);
            return data ? JSON.parse(data) : [];
        }
        catch (error) {
            console.error('Failed to parse localStorage data:', error);
            return [];
        }
    }
    saveItems(items) {
        try {
            localStorage.setItem(this.storageKey, JSON.stringify(items));
        }
        catch (error) {
            console.error('Failed to save to localStorage:', error);
            throw new Error('Storage quota exceeded or localStorage unavailable');
        }
    }
    applyFilter(items, filter) {
        if (!filter)
            return items;
        if (typeof filter === 'function') {
            return items.filter(filter);
        }
        return items.filter((item) => {
            return Object.entries(filter).every(([key, value]) => {
                return item[key] === value;
            });
        });
    }
    applySort(items, sort) {
        if (!sort)
            return items;
        return [...items].sort((a, b) => {
            const aVal = a[sort.field];
            const bVal = b[sort.field];
            if (aVal < bVal)
                return sort.order === 'asc' ? -1 : 1;
            if (aVal > bVal)
                return sort.order === 'asc' ? 1 : -1;
            return 0;
        });
    }
    async create(item) {
        const items = this.getItems();
        items.push(item);
        this.saveItems(items);
        return item;
    }
    async read(id) {
        const items = this.getItems();
        return items.find((item) => item.id === id) || null;
    }
    async update(id, data) {
        const items = this.getItems();
        const index = items.findIndex((item) => item.id === id);
        if (index === -1) {
            return null;
        }
        items[index] = { ...items[index], ...data };
        this.saveItems(items);
        return items[index];
    }
    async delete(id) {
        const items = this.getItems();
        const initialLength = items.length;
        const filteredItems = items.filter((item) => item.id !== id);
        if (filteredItems.length === initialLength) {
            return false;
        }
        this.saveItems(filteredItems);
        return true;
    }
    async list(options = {}) {
        const { page = 1, limit = 10, filter, sort } = options;
        let items = this.getItems();
        // Apply filtering
        items = this.applyFilter(items, filter);
        // Apply sorting
        items = this.applySort(items, sort);
        // Calculate pagination
        const total = items.length;
        const totalPages = Math.ceil(total / limit);
        const start = (page - 1) * limit;
        const end = start + limit;
        const paginatedItems = items.slice(start, end);
        return {
            items: paginatedItems,
            page,
            limit,
            total,
            totalPages,
        };
    }
    async count(filter) {
        const items = this.getItems();
        const filteredItems = this.applyFilter(items, filter);
        return filteredItems.length;
    }
}

/**
 * Remote API adapter for CRUD operations
 * Makes REST API calls following standard conventions:
 * - GET /resource - list
 * - GET /resource/:id - read
 * - POST /resource - create
 * - PUT /resource/:id - update
 * - DELETE /resource/:id - delete
 */
class RemoteAPIAdapter {
    constructor(config) {
        if (typeof config === 'string') {
            this.baseUrl = config;
            this.headers = { 'Content-Type': 'application/json' };
            this.timeout = 30000;
        }
        else {
            this.baseUrl = config.baseUrl;
            this.headers = config.headers || { 'Content-Type': 'application/json' };
            this.timeout = config.timeout || 30000;
        }
    }
    /**
     * Set or update auth token
     */
    setAuthToken(token) {
        this.headers['Authorization'] = `Bearer ${token}`;
    }
    /**
     * Update headers
     */
    setHeaders(headers) {
        this.headers = { ...this.headers, ...headers };
    }
    async request(method, path, body, queryParams) {
        const url = new URL(path, this.baseUrl);
        // Add query parameters
        if (queryParams) {
            Object.entries(queryParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    url.searchParams.append(key, String(value));
                }
            });
        }
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);
        try {
            const response = await fetch(url.toString(), {
                method,
                headers: this.headers,
                body: body ? JSON.stringify(body) : undefined,
                signal: controller.signal,
            });
            clearTimeout(timeoutId);
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`API Error (${response.status}): ${errorText}`);
            }
            // Handle empty responses (e.g., DELETE)
            const text = await response.text();
            return text ? JSON.parse(text) : {};
        }
        catch (error) {
            clearTimeout(timeoutId);
            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    throw new Error(`Request timeout after ${this.timeout}ms`);
                }
                throw error;
            }
            throw new Error('Unknown error occurred');
        }
    }
    async create(item) {
        return this.request('POST', '/', item);
    }
    async read(id) {
        try {
            return await this.request('GET', `/${id}`);
        }
        catch (error) {
            if (error instanceof Error && error.message.includes('404')) {
                return null;
            }
            throw error;
        }
    }
    async update(id, data) {
        try {
            return await this.request('PUT', `/${id}`, data);
        }
        catch (error) {
            if (error instanceof Error && error.message.includes('404')) {
                return null;
            }
            throw error;
        }
    }
    async delete(id) {
        try {
            await this.request('DELETE', `/${id}`);
            return true;
        }
        catch (error) {
            if (error instanceof Error && error.message.includes('404')) {
                return false;
            }
            throw error;
        }
    }
    async list(options = {}) {
        const { page = 1, limit = 10, filter, sort } = options;
        const queryParams = {
            page,
            limit,
        };
        // Add filter params
        if (filter && typeof filter === 'object') {
            Object.entries(filter).forEach(([key, value]) => {
                queryParams[`filter[${key}]`] = value;
            });
        }
        // Add sort params
        if (sort) {
            queryParams.sortBy = sort.field;
            queryParams.sortOrder = sort.order;
        }
        return this.request('GET', '/', undefined, queryParams);
    }
    async count(filter) {
        const queryParams = {};
        if (filter) {
            Object.entries(filter).forEach(([key, value]) => {
                queryParams[`filter[${key}]`] = value;
            });
        }
        const result = await this.request('GET', '/count', undefined, queryParams);
        return result.count;
    }
}

export { CRUDStorage, LocalStorageAdapter, RemoteAPIAdapter };
//# sourceMappingURL=index.esm.js.map
